import {
  measuredActions,
  type MeasuredWorkspaceActions,
} from "./round-workspace-measured.js";
import { useProfileRefresh } from "./use-profile-refresh.js";
import { correctionActions } from "./round-workspace-correction.js";
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { BootstrapPayload } from "../../shared/contracts/bootstrap.js";
import type {
  RoundConflictSnapshot,
  RoundSnapshot,
} from "../../domain/round/round-reducer.js";
import { loadBootstrap } from "../api/bootstrap-api.js";
import { loadWorkspaceAccount } from "./round-workspace-account.js";
import { resolveRoundConflict, roundSyncApi } from "../api/round-api.js";
import { getDeviceId, nextDeviceSequence } from "../data/device-identity.js";
import { deviceAccountCache, roundJournal } from "../data/local-database.js";
import { RoundSyncEngine } from "../sync/round-sync-engine.js";
import type { CreateLocalRoundInput } from "../sync/round-sync-engine.js";
import {
  abandonRound,
  finishRound,
  requireRound,
  scheduleAutoSync,
  syncRound,
  transferScorekeeper,
  useAutoSync,
  type ActionContext,
} from "./round-workspace-sync.js";
import { useLiveRound } from "./use-live-round.js";
import { useLiveQuery } from "dexie-react-hooks";
import {
  contestActions,
  type ContestWorkspaceActions,
} from "./round-workspace-contests.js";
import type { HoleEntryInput } from "../../domain/round/round-types.js";
import {
  useSeasonWorkspace,
  type SeasonWorkspaceActions,
} from "./round-workspace-season.js";
import { SeasonRoundStartStore } from "../sync/season-round-start-store.js";
import {
  useImportWorkspace,
  type ImportWorkspaceActions,
} from "./round-workspace-import.js";

export type RoundSetupInput = Omit<CreateLocalRoundInput, "roundId">;

export interface RoundWorkspace
  extends
    ContestWorkspaceActions,
    SeasonWorkspaceActions,
    ImportWorkspaceActions,
    MeasuredWorkspaceActions {
  accountId: string;
  authenticated: boolean;
  bootstrap: BootstrapPayload | null;
  snapshot: RoundSnapshot | null;
  conflicts: RoundConflictSnapshot[];
  online: boolean;
  queued: boolean;
  scorekeepingTransferPending: boolean;
  conflicted: boolean;
  busy: boolean;
  message: string;
  lastLive: string;
  startRound(input: RoundSetupInput): Promise<boolean>;
  startQuickRound(): Promise<void>;
  setScore(
    targetId: string,
    holeNumber: number,
    strokes: number,
  ): Promise<void>;
  setMetric(participantId: string, value: number): Promise<void>;
  setHoleEntry(
    participantId: string,
    holeNumber: number,
    entry: HoleEntryInput,
  ): Promise<void>;
  declareWinner(participantIds: string[]): Promise<void>;
  syncRound(): Promise<void>;
  finishRound(): Promise<void>;
  acceptRecordedSnapshot(snapshot: RoundSnapshot): Promise<void>;
  abandonRound(): Promise<boolean>;
  transferScorekeeper(nextAccountId: string): Promise<boolean>;
  resolveConflict(resolution: "keep-server" | "use-local"): Promise<void>;
  setMessage: Dispatch<SetStateAction<string>>;
}

export function useRoundWorkspace(
  accountId: string,
  authenticated = true,
): RoundWorkspace {
  const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
  const [snapshot, setSnapshot] = useState<RoundSnapshot | null>(null);
  const [queued, setQueued] = useState(false);
  const [conflicted, setConflicted] = useState(false);
  const [conflicts, setConflicts] = useState<RoundConflictSnapshot[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const online = useOnlineState();
  const engine = useRoundEngine(accountId);
  const scorekeepingTransferPending = useLiveQuery(
    () => roundJournal.hasPendingTransfer(accountId),
    [accountId],
    false,
  );
  const [autoSync, contextRef] = useAutoSync(engine);
  const lastLive = useLiveRound(
    accountId,
    snapshot,
    queued,
    authenticated,
    setSnapshot,
  );
  useAccountLoad(
    accountId,
    setBootstrap,
    setSnapshot,
    setQueued,
    setConflicted,
    setConflicts,
    setMessage,
    authenticated,
  );

  const context: ActionContext = {
    accountId,
    authenticated,
    online,
    bootstrap,
    snapshot,
    conflicts,
    engine,
    autoSync,
    setSnapshot,
    setQueued,
    setConflicted,
    setConflicts,
    setMessage,
  };
  useQueuedSync(context, contextRef, queued);
  const extensions = useRoundExtensions(context, setBusy);
  return {
    accountId,
    authenticated,
    bootstrap,
    snapshot,
    online,
    queued,
    scorekeepingTransferPending,
    conflicted,
    conflicts,
    busy,
    message,
    lastLive,
    ...correctionActions(accountId, () => contextRef.current),
    ...extensions,
    ...roundActions(context, setBusy, setMessage),
    ...measuredActions(context, (action) =>
      runAction(action, setBusy, setMessage),
    ),
    ...contestActions(context, (action) =>
      runWithResult(action, setBusy, setMessage),
    ),
    setMessage,
  };
}

function useRoundExtensions(
  context: ActionContext,
  setBusy: Dispatch<SetStateAction<boolean>>,
) {
  const season = useSeasonWorkspace(context, setBusy);
  const imports = useImportWorkspace(context, setBusy);
  return { ...season, ...imports };
}

function useQueuedSync(
  context: ActionContext,
  contextRef: { current: ActionContext | null },
  queued: boolean,
): void {
  contextRef.current = context;
  const { authenticated, online, autoSync } = context;
  const conflicted = context.conflicts.length > 0;
  useEffect(() => {
    if (!authenticated || !online || !queued || conflicted) return;
    autoSync.schedule();
    // Retry transient service failures without asking the golfer to manage delivery.
    const retry = setInterval(() => autoSync.schedule(), 30_000);
    return () => clearInterval(retry);
  }, [authenticated, online, queued, conflicted, autoSync]);
}

function roundActions(
  context: ActionContext,
  setBusy: Dispatch<SetStateAction<boolean>>,
  setMessage: Dispatch<SetStateAction<string>>,
): Pick<
  RoundWorkspace,
  | "startRound"
  | "startQuickRound"
  | "setScore"
  | "setMetric"
  | "setHoleEntry"
  | "declareWinner"
  | "syncRound"
  | "finishRound"
  | "abandonRound"
  | "transferScorekeeper"
  | "resolveConflict"
> {
  const run = (action: () => Promise<void>) =>
    runAction(action, setBusy, setMessage);
  return {
    startRound: (input) =>
      runWithResult(() => startRound(context, input), setBusy, setMessage),
    startQuickRound: () => run(() => startQuickRound(context)),
    setScore: (targetId, holeNumber, strokes) =>
      run(() => saveScore(context, targetId, holeNumber, strokes)),
    setMetric: (participantId, value) =>
      run(() => saveMetric(context, participantId, value)),
    setHoleEntry: (participantId, holeNumber, entry) =>
      run(() => saveHoleEntry(context, participantId, holeNumber, entry)),
    declareWinner: (participantIds) =>
      run(() => declareWinner(context, participantIds)),
    syncRound: () => run(() => syncRound(context)),
    finishRound: () => run(() => finishRound(context)),
    abandonRound: () =>
      runWithResult(() => abandonRound(context), setBusy, setMessage),
    transferScorekeeper: (nextAccountId) =>
      runWithResult(
        () => transferScorekeeper(context, nextAccountId),
        setBusy,
        setMessage,
      ),
    resolveConflict: (resolution) =>
      run(() => resolveConflict(context, resolution)),
  };
}

function useRoundEngine(accountId: string): RoundSyncEngine {
  const deviceId = useMemo(() => getDeviceId(), []);
  return useMemo(
    () =>
      new RoundSyncEngine({
        journal: roundJournal,
        api: roundSyncApi,
        accountId,
        deviceId,
        nextSequence: () => nextDeviceSequence(deviceId),
        nextEventId: () => crypto.randomUUID(),
      }),
    [accountId, deviceId],
  );
}

function useAccountLoad(
  accountId: string,
  setBootstrap: Dispatch<SetStateAction<BootstrapPayload | null>>,
  setSnapshot: Dispatch<SetStateAction<RoundSnapshot | null>>,
  setQueued: Dispatch<SetStateAction<boolean>>,
  setConflicted: Dispatch<SetStateAction<boolean>>,
  setConflicts: Dispatch<SetStateAction<RoundConflictSnapshot[]>>,
  setMessage: Dispatch<SetStateAction<string>>,
  authenticated: boolean,
): void {
  useProfileRefresh(accountId, authenticated, setBootstrap);
  useEffect(() => {
    let active = true;
    void loadWorkspaceAccount(accountId, {
      loadBootstrap,
      cache: deviceAccountCache,
      journal: roundJournal,
      isCurrent: () => active,
    })
      .then(({ data, round, hasQueued, hasConflicts, usedCache }) => {
        if (!active) return;
        setBootstrap(data);
        setSnapshot(round);
        setQueued(hasQueued);
        setConflicted(hasConflicts);
        setConflicts(data.openConflicts);
        setMessage(usedCache ? cachedAccountMessage(authenticated) : "");
      })
      .catch((error: unknown) => active && setMessage(errorMessage(error)));
    return () => {
      active = false;
    };
  }, [
    accountId,
    setBootstrap,
    setConflicted,
    setConflicts,
    setMessage,
    setQueued,
    setSnapshot,
    authenticated,
  ]);
}

function useOnlineState(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

async function startQuickRound(context: ActionContext): Promise<void> {
  const participant = context.bootstrap?.participants[0];
  if (!participant) throw new Error("Your player profile is not ready.");
  await startRound(context, {
    participants: [
      {
        id: participant.id,
        displayName: participant.displayName,
        handicapIndex: null,
        teamId: null,
      },
    ],
    course: {
      id: "manual-quick-round",
      source: "manual",
      name: "Quick round",
      teeName: "Member",
      rating: null,
      slope: null,
      holes: [{ number: 1, par: 4, strokeIndex: 1 }],
    },
    conditions: { playedAt: null, cart: null, weather: null },
    rules: {
      kind: "outdoor",
      format: "stroke-play",
      handicapMode: "off",
      metric: null,
    },
  });
}

async function startRound(
  context: ActionContext,
  input: RoundSetupInput,
): Promise<void> {
  if (new SeasonRoundStartStore(localStorage).pending(context.accountId))
    throw new Error(
      "Continue or abandon the saved season setup before starting another round.",
    );
  const roundId = crypto.randomUUID();
  await context.engine.createRound({ roundId, ...input });
  context.setSnapshot(await context.engine.startRound(roundId));
  context.setQueued(true);
  context.setMessage("Round saved on this device.");
  scheduleAutoSync(context);
}

async function saveScore(
  context: ActionContext,
  targetId: string,
  holeNumber: number,
  strokes: number,
): Promise<void> {
  const snapshot = requireRound(context);
  context.setSnapshot(
    await context.engine.setScore(
      snapshot.roundId,
      targetId,
      holeNumber,
      strokes,
    ),
  );
  context.setQueued(true);
  context.setMessage("Score saved on this device.");
  scheduleAutoSync(context);
}

async function saveMetric(
  context: ActionContext,
  participantId: string,
  value: number,
): Promise<void> {
  const snapshot = requireRound(context);
  context.setSnapshot(
    await context.engine.setMetric(snapshot.roundId, participantId, value),
  );
  context.setQueued(true);
  context.setMessage("Result saved on this device.");
  scheduleAutoSync(context);
}

async function saveHoleEntry(
  context: ActionContext,
  participantId: string,
  holeNumber: number,
  entry: HoleEntryInput,
): Promise<void> {
  const snapshot = requireRound(context);
  context.setSnapshot(
    await context.engine.setHoleEntry(
      snapshot.roundId,
      participantId,
      holeNumber,
      entry,
    ),
  );
  context.setQueued(true);
  context.setMessage("Hole saved on this device.");
  scheduleAutoSync(context);
}

async function declareWinner(
  context: ActionContext,
  participantIds: string[],
): Promise<void> {
  const snapshot = requireRound(context);
  context.setSnapshot(
    await context.engine.declareWinner(snapshot.roundId, participantIds),
  );
  context.setQueued(true);
  context.setMessage("Winner saved on this device.");
  scheduleAutoSync(context);
}

async function resolveConflict(
  context: ActionContext,
  resolution: "keep-server" | "use-local",
): Promise<void> {
  const selected = context.conflicts[0] ?? null;
  if (!selected) throw new Error("The score conflict is not available.");
  context.setSnapshot(await resolveRoundConflict(selected.id, resolution));
  context.engine.clearConflicts();
  context.setConflicts([]);
  context.setConflicted(false);
  context.setMessage("The score conflict is resolved.");
}

async function runAction(
  action: () => Promise<void>,
  setBusy: Dispatch<SetStateAction<boolean>>,
  setMessage: Dispatch<SetStateAction<string>>,
): Promise<void> {
  setBusy(true);
  setMessage("");
  try {
    await action();
  } catch (error) {
    setMessage(errorMessage(error));
  } finally {
    setBusy(false);
  }
}

async function runWithResult(
  action: () => Promise<void>,
  setBusy: Dispatch<SetStateAction<boolean>>,
  setMessage: Dispatch<SetStateAction<string>>,
): Promise<boolean> {
  setBusy(true);
  setMessage("");
  try {
    await action();
    return true;
  } catch (error) {
    setMessage(errorMessage(error));
    return false;
  } finally {
    setBusy(false);
  }
}

function cachedAccountMessage(authenticated: boolean): string {
  if (!navigator.onLine) return "Offline. Your changes stay on this device.";
  return authenticated
    ? "The service is unavailable. Your saved round is available."
    : "Round saved on this device. Waiting to reconnect.";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The request failed.";
}
